import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { CmsThemeConfig, DEFAULT_THEME_CONFIG, BrandColors, DesignSystem } from '@/config/cmsThemePresets';

export const useCmsThemeConfig = () => {
  const queryClient = useQueryClient();

  const { data: themeConfig = DEFAULT_THEME_CONFIG, isLoading } = useQuery({
    queryKey: ['cms-theme-config'],
    queryFn: async (): Promise<CmsThemeConfig> => {
      const { data, error } = await pmsFrom('cms_theme_config')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !data) return DEFAULT_THEME_CONFIG;

      // Parse custom_colors from JSON
      let custom_colors: BrandColors | null = null;
      if (data.custom_colors && typeof data.custom_colors === 'object' && !Array.isArray(data.custom_colors)) {
        const cc = data.custom_colors as Record<string, unknown>;
        if (cc.primary && cc.secondary && cc.accent) {
          custom_colors = {
            primary: cc.primary as string,
            secondary: cc.secondary as string,
            accent: cc.accent as string,
          };
        }
      }

      // Parse custom_fonts from JSON
      let custom_fonts: CmsThemeConfig['custom_fonts'] = null;
      if (data.custom_fonts && typeof data.custom_fonts === 'object' && !Array.isArray(data.custom_fonts)) {
        const cf = data.custom_fonts as Record<string, unknown>;
        if (cf.heading && cf.body) {
          custom_fonts = {
            heading: cf.heading as string,
            body: cf.body as string,
            headingFileUrl: (cf.headingFileUrl as string) || null,
            bodyFileUrl: (cf.bodyFileUrl as string) || null,
          };
        }
      }

      return {
        active_preset: data.active_preset,
        design_system: ((data as any).design_system || 'modern') as DesignSystem,
        header_style: data.header_style as CmsThemeConfig['header_style'],
        hero_variant: data.hero_variant as CmsThemeConfig['hero_variant'],
        card_style: data.card_style as CmsThemeConfig['card_style'],
        color_palette: data.color_palette as CmsThemeConfig['color_palette'],
        typography_pack: data.typography_pack as CmsThemeConfig['typography_pack'],
        footer_style: data.footer_style as CmsThemeConfig['footer_style'],
        section_card_style: (data.section_card_style || 'elevated') as CmsThemeConfig['section_card_style'],
        testimonial_style: (data.testimonial_style || 'carousel') as CmsThemeConfig['testimonial_style'],
        cta_style: (data.cta_style || 'banner') as CmsThemeConfig['cta_style'],
        custom_colors,
        custom_fonts,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

  const updateConfig = useMutation({
    mutationFn: async (config: Partial<CmsThemeConfig>) => {
      // Check if a row exists
      const { data: existing } = await pmsFrom('cms_theme_config')
        .select('id')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const dbPayload: Record<string, unknown> = {};
      if (config.active_preset !== undefined) dbPayload.active_preset = config.active_preset;
      if (config.design_system) dbPayload.design_system = config.design_system;
      if (config.header_style) dbPayload.header_style = config.header_style;
      if (config.hero_variant) dbPayload.hero_variant = config.hero_variant;
      if (config.card_style) dbPayload.card_style = config.card_style;
      if (config.color_palette) dbPayload.color_palette = config.color_palette;
      if (config.typography_pack) dbPayload.typography_pack = config.typography_pack;
      if (config.footer_style) dbPayload.footer_style = config.footer_style;
      if (config.section_card_style) dbPayload.section_card_style = config.section_card_style;
      if (config.testimonial_style) dbPayload.testimonial_style = config.testimonial_style;
      if (config.cta_style) dbPayload.cta_style = config.cta_style;
      if (config.custom_colors !== undefined) dbPayload.custom_colors = config.custom_colors;
      if (config.custom_fonts !== undefined) dbPayload.custom_fonts = config.custom_fonts;

      if (existing) {
        const { error } = await pmsFrom('cms_theme_config')
          .update(dbPayload)
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await pmsFrom('cms_theme_config')
          .insert(dbPayload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-theme-config'] });
    },
  });

  const applyPreset = useMutation({
    mutationFn: async (presetConfig: CmsThemeConfig) => {
      await updateConfig.mutateAsync(presetConfig);
    },
  });

  return { themeConfig, isLoading, updateConfig, applyPreset };
};
